import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { Eye, EyeOff, CheckCircle2, KeyRound, ArrowLeft, Lock } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { GlassCard } from '../components/GlassCard';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [username, setUsername] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      setTokenError('ไม่พบ Token สำหรับการรีเซ็ตรหัสผ่าน กรุณาใช้ลิงก์ที่ได้รับจากอีเมล');
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await api.get(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        setTokenValid(true);
        setUsername(res.data.username || '');
      } catch (err: any) {
        setTokenValid(false);
        setTokenError(
          err.response?.data?.message ||
            'ลิงก์รีเซ็ตรหัสผ่านนี้ไม่ถูกต้อง ถูกใช้งานไปแล้ว หรือหมดอายุการใช้งานแล้ว (มีอายุ 60 นาที)'
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');

    if (newPassword.length < 6) {
      setErrorMsg('รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });
      setSuccessMsg(res.data.message || 'ตั้งค่ารหัสผ่านใหม่เรียบร้อยแล้ว');
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'เกิดข้อผิดพลาดในการตั้งรหัสผ่านใหม่');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: 6,
        background:
          'radial-gradient(circle at 50% 25%, rgba(30, 64, 175, 0.08) 0%, rgba(248, 250, 252, 0) 70%)',
      }}
    >
      <Container maxWidth="xs">
        <GlassCard sx={{ p: { xs: 3, sm: 4.5 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                mx: 'auto',
                mb: 1.5,
                bgcolor: 'primary.main',
                boxShadow: '0 6px 14px rgba(30, 64, 175, 0.25)',
              }}
            >
              <KeyRound size={26} />
            </Avatar>
            <Typography component="h1" variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
              ตั้งรหัสผ่านใหม่
            </Typography>
            <Typography variant="body2" color="text.secondary">
              TPMS - คณะครุศาสตร์ มรภ.กำแพงเพชร
            </Typography>
          </Box>

          {verifying && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                กำลังตรวจสอบความถูกต้องของลิงก์...
              </Typography>
            </Box>
          )}

          {!verifying && !tokenValid && (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                {tokenError}
              </Alert>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ArrowLeft size={18} />}
                onClick={() => navigate('/login')}
              >
                กลับไปหน้าเข้าสู่ระบบ (Login)
              </Button>
            </Box>
          )}

          {!verifying && tokenValid && successMsg && (
            <Box sx={{ textAlign: 'center' }}>
              <Alert icon={<CheckCircle2 size={18} />} severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                {successMsg}
              </Alert>
              <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')}>
                เข้าสู่ระบบด้วยรหัสผ่านใหม่
              </Button>
            </Box>
          )}

          {!verifying && tokenValid && !successMsg && (
            <form onSubmit={handleSubmit}>
              {username && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    bgcolor: '#EFF6FF',
                    borderRadius: 2,
                    border: '1px solid #BFDBFE',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    บัญชีผู้ใช้งาน:
                  </Typography>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
                    {username}
                  </Typography>
                </Box>
              )}

              {errorMsg && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMsg}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                label="รหัสผ่านใหม่ (New Password)"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="อย่างน้อย 6 ตัวอักษร"
                disabled={submitting}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={18} color="#64748B" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="ยืนยันรหัสผ่านใหม่ (Confirm Password)"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="กรอกรหัสผ่านใหม่อีกครั้ง"
                disabled={submitting}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock size={18} color="#64748B" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 1.5 }}
                disabled={submitting || !newPassword || !confirmPassword}
              >
                {submitting ? 'กำลังบันทึก...' : 'บันทึกรหัสผ่านใหม่'}
              </Button>

              <Button
                fullWidth
                variant="text"
                color="inherit"
                size="small"
                onClick={() => navigate('/login')}
                disabled={submitting}
              >
                ยกเลิกและกลับไปหน้าเข้าสู่ระบบ
              </Button>
            </form>
          )}
        </GlassCard>
      </Container>
    </Box>
  );
};

export default ResetPassword;
